import React, { useEffect, useRef, useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  Play,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { LazyBelowFold } from "@/components/landing/lazy-below-fold";
import type { Region } from "@/types/billing";

export const Route = createFileRoute("/")({
  beforeLoad: async ({ context }) => {
    const session = context.session;
    if (session) {
      throw redirect({ to: "/dashboard" });
    }
  },
  loader: async ({ context }): Promise<{ region: Region }> => {
    const region = context.region;
    return { region };
  },
  component: Index,
});

function Index() {
  const { region } = Route.useLoaderData();
  const videoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (videoRef.current) {
        videoRef.current.style.transform = `translateY(${window.scrollY * 0.15}px)`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background cinematic-noise overflow-x-hidden">
      {/* Section 1: Hero */}
      <section className="relative min-h-[90vh] flex flex-col items-center justify-center overflow-hidden pt-20 pb-10 sm:pb-20">
        <div className="container mx-auto px-4 z-10">
          <div className="max-w-5xl mx-auto text-center space-y-6 sm:space-y-10">
            <h1
              className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-serif tracking-tight leading-[0.9] animate-fade-in"
              style={{ animationDelay: "100ms", animationFillMode: "both" }}
            >
              Generate. Edit.
              <br />
              <span className="text-muted-foreground italic">Iterate.</span>
            </h1>

            <p
              className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-2xl mx-auto font-light leading-relaxed animate-fade-in px-4"
              style={{ animationDelay: "300ms", animationFillMode: "both" }}
            >
              The all-in-one AI image studio — flagship models including FLUX 3, GPT Image 2.5, Qwen Image 3, Seedream 5, and Ideogram V4. Natural-language editing, version history, and exports.
              <br className="hidden sm:block" />
              Go from idea to finished visual in seconds.
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center pt-6 sm:pt-8 animate-fade-in px-4"
              style={{ animationDelay: "500ms", animationFillMode: "both" }}
            >
              <Link to="/signup" preload="intent" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="text-base px-10 h-14 rounded-full font-medium w-full group"
                >
                  Start Creating — Free
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Video Section - Floating beneath text */}
        <div
          ref={videoRef}
          className="container mx-auto px-4 mt-16 sm:mt-20 relative z-20 animate-fade-in will-change-transform"
          style={{
            animationDelay: "700ms",
            animationFillMode: "both",
          }}
        >
          <div className="max-w-4xl mx-auto">
            <HeroVideo />
          </div>
        </div>

        {/* Subtle background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] sm:w-[800px] sm:h-[800px] bg-primary/5 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none -z-10" />
      </section>

      {/* Sections 2-5: Gallery, Features, Pricing, FAQ — lazy loaded */}
      <LazyBelowFold region={region} />

      {/* Section 6: Final CTA */}
      <section className="py-12 sm:py-32 lg:py-48 relative overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 bg-[url('/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8 sm:space-y-12">
            <h2 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif tracking-tight leading-none">
              Your next image is
              <br className="hidden sm:block" />
              <span className="italic text-background/60 sm:inline block mt-2 sm:mt-0">
                one sentence away.
              </span>
            </h2>

            <div className="pt-4 sm:pt-8 px-4">
              <Link
                to="/signup"
                preload="intent"
                className="w-full sm:w-auto inline-block"
              >
                <Button
                  size="lg"
                  className="w-full sm:w-auto text-base sm:text-lg px-8 sm:px-12 h-14 sm:h-16 rounded-full font-medium bg-background text-foreground hover:bg-background/90 hover:scale-105 transition-all duration-300"
                >
                  Try It Free — No Credit Card
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function HeroVideo() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger className="relative w-full group outline-none">
        {/* Soft elegant shadow instead of harsh border */}
        <div className="absolute -inset-2 sm:-inset-4 bg-primary/10 blur-xl sm:blur-2xl rounded-[2rem] sm:rounded-[3rem] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

        <div className="relative aspect-[16/9] sm:aspect-[21/9] rounded-xl sm:rounded-2xl overflow-hidden bg-muted/30">
          <img
            src="/cdn-cgi/image/f=webp,w=1200/landing/video.png"
            className="w-full h-full object-cover transition-transform duration-[3s] ease-out group-hover:scale-[1.02] filter brightness-90 group-hover:brightness-100"
            loading="eager"
            decoding="async"
            fetchpriority="high"
            alt="Hero Video Thumbnail"
          />

          <div className="absolute inset-0 bg-black/10 transition-colors duration-500 group-hover:bg-black/0" />

          {/* Refined Play Button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-background/80 backdrop-blur-md flex items-center justify-center shadow-2xl transition-transform duration-500 group-hover:scale-110 group-hover:bg-background">
              <Play
                className="w-6 h-6 sm:w-8 sm:h-8 text-foreground ml-1"
                strokeWidth={1.5}
              />
            </div>
          </div>
        </div>
      </DialogTrigger>

      <DialogContent
        className="w-[95vw] sm:w-[90vw] md:max-w-5xl p-0 overflow-hidden bg-black border-none rounded-xl"
        showCloseButton={true}
      >
        <div className="aspect-video bg-black flex items-center justify-center">
          {isOpen && (
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube-nocookie.com/embed/7vcQDXU7g3A?si=39j3iCx4VdwcKswt&autoplay=1"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="border-none"
            ></iframe>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
